import type { ReactNode, CSSProperties } from 'react';

type Spacing = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type Align = 'start' | 'center' | 'end' | 'stretch';

interface StackProps {
  children: ReactNode;
  /**
   * Vertical spacing between children
   * @default 'md'
   */
  spacing?: Spacing;
  /**
   * Horizontal alignment of children
   * @default 'stretch'
   */
  align?: Align;
  /**
   * Additional CSS class
   */
  className?: string;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const spacingMap: Record<Spacing, string> = {
  none: '0',
  xs: '0.25rem',
  sm: '0.5rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
  '2xl': '3rem',
};

const alignMap: Record<Align, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
};

/**
 * Stack component for vertical layouts with consistent spacing.
 *
 * @example
 * <Stack spacing="lg" align="center">
 *   <Heading>Title</Heading>
 *   <p>Description</p>
 *   <Button>Action</Button>
 * </Stack>
 */
export function Stack({
  children,
  spacing = 'md',
  align = 'stretch',
  className = '',
  'data-testid': testId,
}: StackProps) {
  const style: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: spacingMap[spacing],
    alignItems: alignMap[align],
  };

  return (
    <div className={className} style={style} data-testid={testId}>
      {children}
    </div>
  );
}
